import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Paper, Stack, Typography, TextField, MenuItem, Avatar, Switch, FormControlLabel } from "@mui/material";
import {
  FilterAltOutlined,
  CalendarMonthOutlined,
  DownloadOutlined,
  KeyboardArrowDownOutlined,
  CloseRounded,
  PeopleOutlineOutlined,
  DirectionsCarOutlined,
  CheckCircleOutlineRounded,
  CancelOutlined,
  AccessTimeOutlined,
  CircleOutlined,
  ArrowRightAltRounded,
  ArrowOutwardRounded,
  ListAltOutlined,
  BusinessOutlined,
  LocalTaxiOutlined,
  PersonOutlineOutlined,
  EventNoteOutlined,
  NotesOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import DriverVehicleAssignmentsService from "../../Services/DriverVehicleAssignments";
import DriverApi from "../../Services/Driver";
import VehicleApi from "../../Services/Vehicle";
import { apiUrl } from "../../Constants";
import "./index.css";

const emptyTimeline = [];

const pickArray = (payload) => {
  const stack = [payload];

  while (stack.length) {
    const current = stack.shift();
    if (Array.isArray(current)) {
      return current;
    }
    if (current && typeof current === "object") {
      stack.push(...Object.values(current));
    }
  }

  return [];
};

const pickNestedArray = (res) => {
  const payload = res?.data;
  const candidates = [
    payload?.data?.data,
    payload?.data,
    payload?.data?.rows,
    payload?.data?.items,
    payload?.rows,
    payload?.items,
    payload?.results,
  ];

  return candidates.find((value) => Array.isArray(value)) || [];
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

const statusStyle = {
  Active: { bg: "#dcfce7", color: "#15803d" },
  Terminée: { bg: "#dbeafe", color: "#2563eb" },
  Annulée: { bg: "#fee2e2", color: "#dc2626" },
};

function StatCard({ stat }) {
  const Icon = stat.icon;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.1,
        borderRadius: 3,
        background: stat.accent,
        border: "1px solid rgba(15, 23, 42, 0.05)",
        boxShadow: "none",
        display: "grid",
        gap: 0.6,
        minHeight: 112,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: stat.iconBg, display: "grid", placeItems: "center", color: stat.iconColor }}>
          <Icon fontSize="small" />
        </Box>
        <Typography sx={{ fontWeight: 700, color: "#10233f" }}>{stat.label}</Typography>
      </Stack>
      <Box>
        <Typography sx={{ fontSize: 28, lineHeight: 1, fontWeight: 900, color: "#10233f" }}>{stat.value}</Typography>
        <Typography sx={{ color: "#64748b", mt: 0.5, fontSize: 13 }}>{stat.helper}</Typography>
      </Box>
    </Paper>
  );
}

function DetailRow({ icon: Icon, label, value, valueColor = "#10233f", rightBadge }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between" sx={{ py: 1.05 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Icon sx={{ color: "#64748b", fontSize: 18 }} />
        <Typography sx={{ color: "#334155", fontWeight: 600 }}>{label}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ color: valueColor, fontWeight: 700, textAlign: "right" }}>{value}</Typography>
        {rightBadge}
      </Stack>
    </Stack>
  );
}

export default function DriverVehicleAssignments() {
  const [selectedId, setSelectedId] = useState(null);
  const [timelineView, setTimelineView] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [inactiveTotal, setInactiveTotal] = useState(0);
  const [driverOptions, setDriverOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [driverFilter, setDriverFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");

  useEffect(() => {
    let isMounted = true;
    const page = paginationModel.page + 1;
    const pageSize = paginationModel.pageSize;

    const loadAssignments = async () => {
      setLoading(true);
      try {
        const response = await DriverVehicleAssignmentsService.getDriverVehicleAssignments({ page, limit: pageSize });

        const payload = response.data;
        const items = pickArray(payload);
        const apiInactiveTotal =
          payload?.data?.inactive_total ??
          payload?.inactive_total ??
          payload?.data?.inactiveTotal ??
          payload?.inactiveTotal ??
          items.filter((item) => {
            const statusValue = String(item?.status || item?.assignment_status || item?.state || "").toLowerCase();
            return statusValue.includes("inactive") || statusValue.includes("done") || statusValue.includes("finish") || statusValue.includes("term");
          }).length;
        if (!isMounted) {
          return;
        }

        setRows(
          items.map((item, index) => {
            const driverRelation =
              item?.users_driver_vehicle_assignments_driver_idTousers ||
              item?.driver ||
              item?.user ||
              {};
            const driverName =
              driverRelation?.full_name ||
              item?.driver?.full_name ||
              item?.driver_name ||
              "Chauffeur";
            const driverCode =
              driverRelation?.code ||
              item?.driver?.code ||
              item?.driver_code ||
              item?.code ||
              "";
            const driverAvatarUrl =
              driverRelation?.avatar_url ||
              item?.driver?.avatar_url ||
              item?.driver_avatar_url ||
              item?.avatar_url ||
              "";
            const resolvedDriverAvatarUrl =
              driverAvatarUrl && !driverAvatarUrl.startsWith("http://") && !driverAvatarUrl.startsWith("https://")
                ? `${apiUrl}${driverAvatarUrl.startsWith("/") ? "" : "/"}${driverAvatarUrl}`
                : driverAvatarUrl;
            const vehicleRelation =
              item?.vehicles ||
              item?.vehicle ||
              {};
            const vehicleName =
              vehicleRelation?.vehicle_name ||
              item?.vehicle_name ||
              vehicleRelation?.name ||
              item?.vehicle?.name ||
              "Véhicule";
            const vehicleNo =
              vehicleRelation?.vehicle_no ||
              item?.vehicle_no ||
              item?.vehicle?.vehicle_no ||
              item?.vehicle?.plate ||
              item?.vehicle_plate ||
              item?.vehicle?.registration_number ||
              "";
            const vehicleImageUrl =
              vehicleRelation?.vehicle_image ||
              vehicleRelation?.vehicle_image_url ||
              item?.vehicle?.vehicle_image ||
              item?.vehicle?.vehicle_image_url ||
              item?.vehicle_image ||
              item?.vehicle_image_url ||
              "";
            const resolvedVehicleImageUrl =
              vehicleImageUrl && !vehicleImageUrl.startsWith("http://") && !vehicleImageUrl.startsWith("https://")
                ? `${apiUrl}${vehicleImageUrl.startsWith("/") ? "" : "/"}${vehicleImageUrl}`
                : vehicleImageUrl;
            const seatsValue = item?.vehicle?.seats || item?.vehicle_seats || item?.seats || "";
            const start = item?.start_date || item?.startDate || item?.assignment_start || item?.assigned_at || item?.created_at || "";
            const end = item?.end_date || item?.endDate || item?.assignment_end || item?.expected_end || item?.planned_end || "En cours";
            const statusRaw = item?.status || item?.assignment_status || item?.state || "Active";
            const normalizedStatus = String(statusRaw).toLowerCase();
            const status = normalizedStatus.includes("cancel") ? "Annulée" : normalizedStatus.includes("done") || normalizedStatus.includes("finish") || normalizedStatus.includes("term") ? "Terminée" : "Active";
            const assignedByRelation =
              item?.users_driver_vehicle_assignments_assigned_byTousers ||
              item?.assigned_by ||
              item?.created_by ||
              {};
            const assignedByName =
              assignedByRelation?.full_name ||
              item?.assigned_by?.name ||
              item?.assigned_by_name ||
              item?.created_by?.name ||
              item?.created_by_name ||
              "—";
            const assignedByDate = item?.assigned_by?.date || item?.assigned_by_date || item?.created_at || "";
            const duration = item?.duration || item?.assignment_duration || "—";

            return {
              id: item?.id ?? item?._id ?? index + 1,
              raw: item,
              driver: {
                name: driverName,
                code: driverCode,
                phone: item?.driver?.phone || item?.driver_phone || "",
                avatar: (driverName || "Chauffeur").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase(),
                avatarUrl: resolvedDriverAvatarUrl,
              },
              vehicle: {
                name: vehicleName,
                plate: vehicleNo,
                seats: seatsValue ? `${seatsValue}${String(seatsValue).includes("place") ? "" : " places"}` : "",
                avatar: "VH",
                imageUrl: resolvedVehicleImageUrl,
              },
              period: formatDateTime(start),
              status,
              end: end === "En cours" ? end : formatDateTime(end),
              duration,
              assignedBy: {
                name: assignedByName,
                date: assignedByDate,
                avatar: (assignedByName || "—").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase(),
              },
              timeline: item?.timeline || [],
              details: {
                start,
                plannedEnd: end,
                actualEnd: item?.actual_end || item?.actualEnd || "—",
                type: item?.type || item?.assignment_type || "—",
                assignedBy: assignedByName,
                reason: item?.reason || item?.note || item?.motif || "—",
                notes: item?.notes || item?.comment || "—",
              },
            };
          })
        );
        setInactiveTotal(apiInactiveTotal);
      } catch (error) {
        if (isMounted) {
          setRows([]);
          setInactiveTotal(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAssignments();

    return () => {
      isMounted = false;
    };
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    let isMounted = true;

    const loadFilterOptions = async () => {
      try {
        const [driversRes, vehiclesRes] = await Promise.all([
          DriverApi.getDriverList({ page: 1, limit: "unlimited", search: "" }),
          VehicleApi.getVehicleList({ page: 1, limit: "unlimited", search: "" }),
        ]);

        if (!isMounted) {
          return;
        }

        const drivers = pickNestedArray(driversRes).map((driver, index) => ({
          value: driver?.id ?? driver?._id ?? index + 1,
          label: driver?.full_name || `${driver?.first_name || ""} ${driver?.last_name || ""}`.trim() || driver?.name || "Chauffeur",
        }));

        const vehicles = pickNestedArray(vehiclesRes).map((vehicle, index) => ({
          value: vehicle?.id ?? vehicle?._id ?? index + 1,
          label: vehicle?.vehicle_name || vehicle?.name || "Véhicule",
        }));

        setDriverOptions(drivers);
        setVehicleOptions(vehicles);
      } catch (error) {
        if (isMounted) {
          setDriverOptions([]);
          setVehicleOptions([]);
        }
      }
    };

    loadFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const selected = selectedId ? rows.find((row) => row.id === selectedId) || null : null;
  const rowCount = rows.length;
  const activeCount = rows.filter((row) => row.status === "Active").length;
  const dynamicStats = [
    {
      label: "Total assignments",
      value: String(rowCount),
      helper: "Toutes périodes",
      accent: "linear-gradient(180deg, #edf9ef 0%, #eefbf2 100%)",
      iconBg: "#dff4e4",
      iconColor: "#0f8a48",
      icon: PeopleOutlineOutlined,
    },
    {
      label: "Assignments actives",
      value: String(activeCount),
      helper: "En cours actuellement",
      accent: "linear-gradient(180deg, #eff6ff 0%, #f4f8ff 100%)",
      iconBg: "#deebff",
      iconColor: "#2563eb",
      icon: CheckCircleOutlineRounded,
    },
    {
      label: "Assignments terminées",
      value: String(inactiveTotal),
      helper: "Sur la période sélectionnée",
      accent: "linear-gradient(180deg, #fff8e8 0%, #fff9ef 100%)",
      iconBg: "#fdeab7",
      iconColor: "#f59e0b",
      icon: EventNoteOutlined,
    },
  ];
  const columns = useMemo(
    () => [
      {
        field: "driver",
        headerName: "Chauffeur",
        flex: 1.45,
        minWidth: 240,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
                <Avatar
                  src={params.row.driver.avatarUrl || undefined}
                  alt={params.row.driver.name}
                  sx={{ bgcolor: "#e8f5ec", color: "#0b7a45", width: 38, height: 38, fontWeight: 800 }}
                >
              {!params.row.driver.avatarUrl ? params.row.driver.avatar : null}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.2 }}>{params.row.driver.name}</Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>{params.row.driver.code}</Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "vehicle",
        headerName: "Véhicule",
        flex: 1.35,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={params.row.vehicle.imageUrl || undefined}
              alt={params.row.vehicle.name}
              variant="rounded"
              sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: "#f8fafc", color: "#0b7a45" }}
            >
              {!params.row.vehicle.imageUrl ? <LocalTaxiOutlined fontSize="small" /> : null}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.2 }}>{params.row.vehicle.name}</Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>{params.row.vehicle.plate}</Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "period",
        headerName: "Période d'assignation",
        flex: 1.35,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.35 }}>
            <Stack direction="row" spacing={0.9} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: params.row.status === "Active" ? "#16a34a" : "#cbd5e1" }} />
              <Typography sx={{ fontWeight: 700, color: "#10233f" }}>{params.row.period}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <ArrowRightAltRounded sx={{ color: "#94a3b8", fontSize: 16 }} />
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>{params.row.end}</Typography>
            </Stack>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Statut",
        width: 120,
        sortable: false,
        renderCell: (params) => {
          const style = statusStyle[params.row.status] || statusStyle.Active;
          return <Chip label={params.row.status} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 800, borderRadius: 1.5 }} />;
        },
      },
      { field: "duration", headerName: "Durée", width: 95, sortable: false, renderCell: (params) => <Typography sx={{ fontWeight: 700, color: "#10233f" }}>{params.row.duration}</Typography> },
      {
        field: "assignedBy",
        headerName: "Assigné par",
        flex: 1.25,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Avatar sx={{ bgcolor: "#eef2ff", color: "#7c3aed", width: 30, height: 30, fontSize: 12 }}>{params.row.assignedBy.avatar}</Avatar>
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.2 }}>{params.row.assignedBy.name}</Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>{params.row.assignedBy.date}</Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        renderCell: (params) => (
          <Button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedId(params.row.id);
            }}
            sx={{ minWidth: 36, width: 36, height: 36, borderRadius: 2, color: "#64748b" }}
          >
            <VisibilityOutlined fontSize="small" />
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <Box className="dva-page" sx={{ p: { xs: 1.5, md: 1.5 } }}>
      <Box className="dva-shell">
        <Stack className="dva-header" direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography className="dva-title">Historique des assignations</Typography>
            <Typography className="dva-subtitle">Consultez l'historique complet des assignations de chauffeurs à des véhicules.</Typography>
          </Box>
          <Button
            variant="contained"
            endIcon={<KeyboardArrowDownOutlined />}
            startIcon={<DownloadOutlined />}
            className="dva-export-btn"
          >
            Exporter
          </Button>
        </Stack>

        <Paper elevation={0} className="dva-filters">
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ lg: "center" }}>
            <Box sx={{ flex: 1.2 }}>
              <Typography className="dva-filters-label">Période</Typography>
              <TextField
                fullWidth
                value="01/07/2026  →  29/07/2026"
                InputProps={{ startAdornment: <CalendarMonthOutlined sx={{ color: "#10233f", mr: 1 }} fontSize="small" /> }}
                className="dva-filter-input"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography className="dva-filters-label">Chauffeur</Typography>
              <TextField select fullWidth value={driverFilter} onChange={(e) => setDriverFilter(e.target.value)} className="dva-filter-input">
                <MenuItem value="all">Tous les chauffeurs</MenuItem>
                {driverOptions.map((driver) => (
                  <MenuItem key={driver.value} value={driver.value}>
                    {driver.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography className="dva-filters-label">Véhicule</Typography>
              <TextField select fullWidth value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)} className="dva-filter-input">
                <MenuItem value="all">Tous les véhicules</MenuItem>
                {vehicleOptions.map((vehicle) => (
                  <MenuItem key={vehicle.value} value={vehicle.value}>
                    {vehicle.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography className="dva-filters-label">Statut</Typography>
              <TextField select fullWidth value="all" className="dva-filter-input">
                <MenuItem value="all">Tous les statuts</MenuItem>
              </TextField>
            </Box>
            <Button
              variant="outlined"
              startIcon={<FilterAltOutlined />}
              className="dva-filter-button"
            >
              Plus de filtres
            </Button>
          </Stack>
        </Paper>

        <Box
          className="dva-content"
          sx={{
            gridTemplateColumns: selected ? { xs: "1fr", lg: "minmax(0, 1.9fr) minmax(0, 0.95fr)" } : "1fr",
          }}
        >
          <Box className="dva-main-column">
            <Box className="dva-stats">
              {dynamicStats.map((stat) => <StatCard key={stat.label} stat={stat} />)}
            </Box>

            <Paper elevation={0} className="dva-list-card">
              <Box className="dva-list-head">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography className="dva-list-title">Liste des assignations</Typography>
                    <Typography className="dva-list-subtitle">{loading ? "Chargement..." : `${rowCount} résultats trouvés`}</Typography>
                  </Box>
                  <Stack className="dva-list-toolbar" direction="row" alignItems="center" spacing={1.2}>
                    <FormControlLabel
                      control={<Switch checked={timelineView} onChange={(e) => setTimelineView(e.target.checked)} />}
                      label="Vue timeline"
                      className="dva-timeline-switch"
                    />
                    <Button variant="outlined" sx={{ minWidth: 48, height: 48, borderRadius: 2, borderColor: "#e3e8ef", color: "#10233f" }}>
                      <ArrowOutwardRounded fontSize="small" />
                    </Button>
                    <Button variant="contained" sx={{ minWidth: 54, height: 48, borderRadius: 2, background: "#148b46", boxShadow: "none", "&:hover": { boxShadow: "none", background: "#11793d" } }}>
                      <ListAltOutlined />
                    </Button>
                    <Button variant="outlined" sx={{ minWidth: 54, height: 48, borderRadius: 2, borderColor: "#e3e8ef", color: "#64748b" }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 0.5 }}>
                        <Box sx={{ width: 5, height: 5, bgcolor: "#64748b" }} />
                        <Box sx={{ width: 5, height: 5, bgcolor: "#64748b" }} />
                        <Box sx={{ width: 5, height: 5, bgcolor: "#64748b" }} />
                        <Box sx={{ width: 5, height: 5, bgcolor: "#64748b" }} />
                      </Box>
                    </Button>
                  </Stack>
                </Stack>
              </Box>
              <Box className={`dva-table-wrap${selected ? "" : " is-full-width"}`}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  rowHeight={84}
                  rowSelectionModel={selectedId ? [selectedId] : []}
                  onRowClick={(params) => setSelectedId(params.row.id)}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": { background: "#f8fafc", borderBottom: "1px solid #e8edf3", minHeight: 56, maxHeight: 56 },
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 800, color: "#475569" },
                    "& .MuiDataGrid-cell": { borderBottom: "1px solid #eef2f7", outline: "none" },
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .MuiDataGrid-row:hover": { background: "#f8fbfd" },
                    "& .MuiDataGrid-overlay": { background: "#fff" },
                  }}
                />
              </Box>
            </Paper>
          </Box>

          {selected ? (
            <Paper elevation={0} className="dva-detail-card">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography className="dva-detail-title">Détails de l'assignation</Typography>
                <Button
                  variant="outlined"
                  className="dva-close-btn"
                  onClick={() => setSelectedId(null)}
                >
                  <CloseRounded fontSize="small" />
                </Button>
              </Stack>

              <Box className="dva-detail-grid">
                <Paper variant="outlined" className="dva-detail-mini">
                  <Typography sx={{ color: "#64748b", fontWeight: 700, mb: 1 }}>Chauffeur</Typography>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Avatar sx={{ width: 52, height: 52, bgcolor: "#e8f5ec", color: "#0b7a45", fontWeight: 800 }}>{selected.driver.avatar}</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: "#10233f" }}>{selected.driver.name}</Typography>
                      <Typography sx={{ color: "#64748b", mt: 0.4 }}>{selected.driver.code}</Typography>
                      <Typography sx={{ color: "#10233f", mt: 0.4, fontSize: 13 }}>{selected.driver.phone}</Typography>
                    </Box>
                  </Stack>
                </Paper>
                <Paper variant="outlined" className="dva-detail-mini">
                  <Typography sx={{ color: "#64748b", fontWeight: 700, mb: 1 }}>Véhicule</Typography>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: "#f8fafc", display: "grid", placeItems: "center", color: "#0b7a45" }}>
                      <DirectionsCarOutlined />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: "#10233f" }}>{selected.vehicle.name}</Typography>
                      <Typography sx={{ color: "#64748b", mt: 0.4 }}>{selected.vehicle.plate}</Typography>
                      <Typography sx={{ color: "#10233f", mt: 0.4, fontSize: 13 }}>{selected.vehicle.seats}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>

              <Box sx={{ borderTop: "1px solid #eef2f7", py: 0.5 }}>
                <DetailRow icon={CalendarMonthOutlined} label="Date de début" value={selected.details.start} />
                <DetailRow icon={AccessTimeOutlined} label="Date de fin prévue" value={selected.details.plannedEnd} />
                <DetailRow icon={CircleOutlined} label="Date de fin réelle" value={selected.details.actualEnd} rightBadge={<Chip label={selected.status === "Active" ? "En cours" : selected.status} size="small" sx={{ bgcolor: selected.status === "Active" ? "#dcfce7" : "#f1f5f9", color: selected.status === "Active" ? "#15803d" : "#334155", fontWeight: 800, borderRadius: 1.5 }} />} />
                <DetailRow icon={EventNoteOutlined} label="Type d'assignation" value={selected.details.type} />
                <DetailRow icon={PersonOutlineOutlined} label="Assigné par" value={selected.details.assignedBy} />
                <DetailRow icon={BusinessOutlined} label="Motif" value={selected.details.reason} />
                <DetailRow icon={NotesOutlined} label="Notes" value={selected.details.notes} />
              </Box>

              <Paper elevation={0} className="dva-events">
                <Typography className="dva-events-title">Historique des événements</Typography>
                <Box sx={{ pl: 0.5, display: "grid", gap: 2 }}>
                  {(selected.timeline.length ? selected.timeline : emptyTimeline).map((item, index) => (
                    <Stack key={`${item.title}-${index}`} direction="row" spacing={1.4} alignItems="flex-start">
                      <Box sx={{ width: 12, pt: 0.6, display: "grid", placeItems: "center" }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: item.color }} />
                        {index !== (selected.timeline.length ? selected.timeline : emptyTimeline).length - 1 ? <Box sx={{ width: 2, flex: 1, minHeight: 28, bgcolor: "#dbe7dc", mt: 0.5 }} /> : null}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: "#10233f" }}>{item.title}</Typography>
                        <Typography sx={{ color: "#64748b", mt: 0.25 }}>{item.date}</Typography>
                        {item.note ? <Typography sx={{ color: "#334155", mt: 0.25 }}>{item.note}</Typography> : null}
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </Paper>

              <Box sx={{ mt: "auto", pt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  className="dva-closing-btn"
                  startIcon={<CancelOutlined />}
                >
                  Clôturer l'assignation
                </Button>
              </Box>
            </Paper>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
